import React, { useEffect, useState } from 'react';
import PageHeader from '../../components/PageHeader/PageHeader';
import KpiCard from '../../components/KpiCard/KpiCard';
import AlertChip from '../../components/AlertChip/AlertChip';
import NuevaSolicitudInterna from './NuevaSolicitudInterna';
import AgregarSolicitud from '../solicitudesbooking/AgregarSolicitud';
import GestionCPO from '../solicitudesbooking/GestionCPO';
import PanelAlerta from './PanelAlerta';
import PlanificacionPI from './PlanificacionPI';
import PlanificacionOC from './PlanificacionOC';
import Bookings from './Bookings';
import { urlApiProd } from '../utiles';
import '../../components/ui.css';
import './controlEmbarques.css';

// Indicadores y alertas usan datos reales (/controlembarques/resumen), salvo "Modificaciones abiertas",
// Cross docking y MAPA, que siguen como mockup hasta definir sus modelos.
const plural = (n, singular, pluralTexto) => `${n} ${n === 1 ? singular : pluralTexto}`;

const alertasMockup = [
    { label: 'Cross docking', valor: 1, tono: 'azul' },
    { label: 'MAPA', valor: 1, tono: 'azul' },
];

const pestanas = [
    { id: 'planificacion-pi', label: 'Planificación PI' },
    { id: 'planificacion-oc', label: 'Planificación OC' },
    { id: 'bookings', label: 'Bookings' },
];

const ControlEmbarques = () => {
    const [pestana, setPestana] = useState('planificacion-pi');
    const [showSolicitudInterna, setShowSolicitudInterna] = useState(false);

    const [datosBooking, setDatosBooking] = useState(null);
    // Se incrementa al cerrar un diálogo de alta para que la pestaña Bookings recargue sus datos
    const [versionDatos, setVersionDatos] = useState(0);
    const [cargandoBooking, setCargandoBooking] = useState(false);
    const [resumen, setResumen] = useState(null);
    const [panelAlerta, setPanelAlerta] = useState(null); // 'coleta' | 'dlc' | 'hc_sin_asignar' | 'pi_sin_booking' | null
    const [ficha, setFicha] = useState(null);
    // Panel al que se vuelve al cerrar un diálogo abierto desde él, y PI preseleccionada en la solicitud interna
    const [panelPrevio, setPanelPrevio] = useState(null);
    const [idProformaInicial, setIdProformaInicial] = useState(null);

    // Indicadores de la cabecera: se recalculan al crear solicitudes o bookings
    useEffect(() => {
        let activo = true;
        const cargarResumen = async () => {
            try {
                const token = localStorage.getItem('token');
                const respuesta = await fetch(`${urlApiProd}/controlembarques/resumen`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!respuesta.ok) throw new Error('No se pudo cargar el resumen');
                const datos = await respuesta.json();
                if (activo) setResumen(datos);
            } catch (error) {
                console.error('Error al cargar el resumen:', error.message);
            }
        };
        cargarResumen();
        return () => { activo = false; };
    }, [versionDatos]);

    const kpis = [
        {
            titulo: 'PI abiertas',
            valor: resumen ? resumen.pi_abiertas.total : '—',
            detalle: resumen ? `${plural(resumen.pi_abiertas.clientes, 'cliente', 'clientes')} con HC por embarcar` : 'cargando…',
            accion: 'ver detalle',
        },
        {
            titulo: 'HC sin booking',
            valor: resumen ? resumen.hc_sin_booking.total : '—',
            detalle: resumen ? `en ${plural(resumen.hc_sin_booking.destinos, 'destino', 'destinos')}` : 'cargando…',
            accion: 'ver detalle',
        },
        {
            titulo: 'Bookings confirmados',
            valor: resumen ? resumen.bookings_confirmados.total : '—',
            detalle: resumen ? `${resumen.bookings_confirmados.con_capacidad_libre} con HC sin asignar` : 'cargando…',
            accion: 'ver detalle',
        },
        {
            titulo: 'Por embarcar 30 días',
            valor: resumen ? `${resumen.por_embarcar_30_dias.hc} HC` : '—',
            detalle: resumen ? `${plural(resumen.por_embarcar_30_dias.bookings, 'booking', 'bookings')} según ETD vigente` : 'cargando…',
            accion: 'ver detalle',
        },
        // Mockup: aún no existe el modelo de cambios solicitados
        { titulo: 'Modificaciones abiertas', valor: '2', detalle: 'sin ejecutar', accion: 'ver detalle' },
    ];

    const datosAlertas = resumen ? resumen.alertas : null;
    const alertas = [
        { label: 'Coleta (DLD)', valor: datosAlertas ? datosAlertas.coleta.total : '—', tono: 'naranja', panel: 'coleta' },
        { label: 'Cut-off carga (DLC)', valor: datosAlertas ? datosAlertas.dlc.total : '—', tono: 'naranja', panel: 'dlc' },
        { label: 'HC sin asignar', valor: datosAlertas ? datosAlertas.hc_sin_asignar.total : '—', tono: 'rojo', panel: 'hc_sin_asignar' },
        {
            label: 'PI sin booking',
            valor: datosAlertas ? datosAlertas.pi_sin_booking.total : '—',
            tono: 'rojo',
            panel: 'pi_sin_booking',
            ayuda: datosAlertas
                ? `Comercial: ${datosAlertas.pi_sin_booking.comercial} · Logística: ${datosAlertas.pi_sin_booking.logistica}`
                : undefined,
        },
        ...alertasMockup,
    ];

    // Datos que necesitan los diálogos de booking (se cargan frescos, igual que en /solicitudes)
    const cargarDatosBooking = async () => {
        const token = localStorage.getItem('token');
        const rutas = ['solicitudesbooking', 'puertos', 'ffw', 'carriers', 'solicitudesinternas'];
        const respuestas = await Promise.all(
            rutas.map((ruta) => fetch(`${urlApiProd}/${ruta}`, { headers: { Authorization: `Bearer ${token}` } }))
        );
        const [solicitudes, datosPuertos, ffw, carriers, internas] = await Promise.all(respuestas.map((res) => {
            if (!res.ok) throw new Error('Error cargando datos de apoyo');
            return res.json();
        }));
        datosPuertos.sort((a, b) => a.nombre.localeCompare(b.nombre));
        return {
            solicitudes,
            puertos: datosPuertos,
            ffw,
            carriers,
            // Solo solicitudes internas con HC pendientes de solicitar
            solicitudesInternas: internas.filter((s) => (s.estado === 0 || s.estado === 1) && s.cantidad !== s.cantidad_solicitada),
        };
    };

    const abrirSolicitarBooking = async () => {
        setCargandoBooking(true);
        try {
            setDatosBooking(await cargarDatosBooking());
            return true;
        } catch (error) {
            console.error('Error al abrir Solicitar booking:', error.message);
            alert('No se pudieron cargar los datos para solicitar el booking.');
            return false;
        } finally {
            setCargandoBooking(false);
        }
    };

    // Acciones del panel "PI sin booking": el panel se cierra (los diálogos quedarían detrás) y se reabre al terminar
    const volverAlPanel = () => {
        if (panelPrevio) {
            setPanelAlerta(panelPrevio);
            setPanelPrevio(null);
        }
    };

    const crearSolicitudDesdePanel = (idProforma) => {
        setPanelPrevio(panelAlerta);
        setPanelAlerta(null);
        setIdProformaInicial(idProforma);
        setShowSolicitudInterna(true);
    };

    const solicitarBookingDesdePanel = async () => {
        setPanelPrevio(panelAlerta);
        setPanelAlerta(null);
        if (!(await abrirSolicitarBooking())) {
            setPanelAlerta(panelAlerta);
            setPanelPrevio(null);
        }
    };

    // Ficha de gestión del booking; al cerrarla se vuelve al panel de alertas desde el que se abrió
    const abrirFicha = async (idSolicitud, volverA) => {
        try {
            const datos = await cargarDatosBooking();
            const booking = datos.solicitudes.find((s) => s.id_solicitud === idSolicitud);
            if (!booking) throw new Error('Booking no encontrado');
            setPanelAlerta(null);
            setFicha({ booking, puertos: datos.puertos, ffw: datos.ffw, carriers: datos.carriers, solicitudesInternas: datos.solicitudesInternas, volverA });
        } catch (error) {
            console.error('Error al abrir la ficha:', error.message);
            alert('No se pudo abrir la ficha del booking.');
        }
    };

    // Registra la fecha de coleta y, si se indica, el nombre del contenedor; el resumen se recalcula y la alerta se apaga
    const registrarColeta = async (idSolicitud, fecha, nombreContenedor) => {
        const token = localStorage.getItem('token');
        const respuesta = await fetch(`${urlApiProd}/controlembarques/booking/${idSolicitud}/coleta`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ fecha_coleta: fecha, nombre_contenedor: nombreContenedor }),
        });
        const resultado = await respuesta.json().catch(() => ({}));
        if (!respuesta.ok) throw new Error(resultado.error || 'No se pudo registrar la coleta');
        setVersionDatos((v) => v + 1);
    };

    // Asigna HC libres de un booking a una PI y una OC; el resumen se recalcula y la alerta se apaga al completarse
    const asignarHc = async (idSolicitud, datos) => {
        const token = localStorage.getItem('token');
        const respuesta = await fetch(`${urlApiProd}/controlembarques/booking/${idSolicitud}/asignar-hc`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(datos),
        });
        const resultado = await respuesta.json().catch(() => ({}));
        if (!respuesta.ok) throw new Error(resultado.error || 'No se pudieron asignar los HC');
        setVersionDatos((v) => v + 1);
    };

    return (
        <div className="container-fluid ui-page">
            <PageHeader
                eyebrow="Logística · Comercial"
                title="Centro de Control de Embarques"
                descripcion="Vista general del estado de los embarques: PI, HC, bookings y alertas pendientes."
                actions={
                    <>
                        <button
                            type="button"
                            className="ui-btn ui-btn-secondary"
                            onClick={() => setShowSolicitudInterna(true)}
                        >
                            + Solicitud interna
                        </button>
                        <button
                            type="button"
                            className="ui-btn ui-btn-primary"
                            onClick={abrirSolicitarBooking}
                            disabled={cargandoBooking}
                        >
                            + Solicitar booking
                        </button>
                    </>
                }
            />

            <div className="row g-3 mb-3">
                {kpis.map((kpi) => (
                    <div className="col-12 col-sm-6 col-xl" key={kpi.titulo}>
                        <KpiCard titulo={kpi.titulo} valor={kpi.valor} detalle={kpi.detalle} accion={kpi.accion} />
                    </div>
                ))}
            </div>

            <div className="card border-0 shadow-sm p-3">
                <div className="d-flex align-items-center flex-wrap gap-2">
                    <span className="fw-semibold me-2">Alertas</span>
                    {alertas.map((alerta) => (
                        <AlertChip
                            key={alerta.label}
                            label={alerta.label}
                            valor={alerta.valor}
                            tono={alerta.tono}
                            ayuda={alerta.ayuda}
                            onClick={alerta.panel ? () => setPanelAlerta(alerta.panel) : undefined}
                        />
                    ))}
                </div>
                <div className="text-end small text-muted mt-2">
                    Clic en una alerta = abre su detalle. Se apagan solas al cumplirse el hito.
                </div>
            </div>

            <div className="card border-0 shadow-sm p-3 mt-3">
                <div className="ce-tabs" role="tablist">
                    {pestanas.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            role="tab"
                            aria-selected={pestana === p.id}
                            className={`ce-tab${pestana === p.id ? ' active' : ''}`}
                            onClick={() => setPestana(p.id)}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                {pestana === 'planificacion-pi' ? (
                    <PlanificacionPI version={versionDatos} />
                ) : pestana === 'planificacion-oc' ? (
                    <PlanificacionOC />
                ) : pestana === 'bookings' ? (
                    <Bookings version={versionDatos} onSolicitarBooking={abrirSolicitarBooking} />
                ) : (
                    <div className="ce-muted py-4 text-center">Esta pestaña se implementará más adelante.</div>
                )}
            </div>

            {panelAlerta && datosAlertas && (
                <PanelAlerta
                    tipo={panelAlerta}
                    items={datosAlertas[panelAlerta].items}
                    onCerrar={() => setPanelAlerta(null)}
                    onFicha={(id) => abrirFicha(id, panelAlerta)}
                    onRegistrarColeta={registrarColeta}
                    onAsignarHc={asignarHc}
                    onCrearSolicitud={crearSolicitudDesdePanel}
                    onSolicitarBooking={solicitarBookingDesdePanel}
                />
            )}

            {ficha && (
                <GestionCPO
                    desde={'bookingexterno'}
                    solicitud={ficha.booking}
                    puertos={ficha.puertos}
                    ffw={ficha.ffw}
                    carriers={ficha.carriers}
                    solicitudesInternas={ficha.solicitudesInternas}
                    cerrar={() => { setPanelAlerta(ficha.volverA); setFicha(null); setVersionDatos((v) => v + 1); }}
                />
            )}

            {datosBooking && (
                <AgregarSolicitud
                    solicitudes={datosBooking.solicitudes}
                    puertos={datosBooking.puertos}
                    ffw={datosBooking.ffw}
                    carriers={datosBooking.carriers}
                    solicitudesInternas={datosBooking.solicitudesInternas}
                    cerrar={() => { setDatosBooking(null); setVersionDatos((v) => v + 1); volverAlPanel(); }}
                />
            )}

            {showSolicitudInterna && (
                <NuevaSolicitudInterna
                    idProformaInicial={idProformaInicial}
                    cerrar={() => { setShowSolicitudInterna(false); setIdProformaInicial(null); volverAlPanel(); }}
                    onGuardada={() => setVersionDatos((v) => v + 1)}
                />
            )}
        </div>
    );
};

export default ControlEmbarques;
